import { MetaConfig } from "./types";

export const buildDefaultFieldValues = (componentFieldsConfig: any[]): Record<string, any> => {
  return componentFieldsConfig.reduce((defaults, field) => {
    const value = field.value ?? field.defaultValue;
    // Optional FILE fields: do not default to "" — that overwrites persisted URLs in Redux/preview
    if (
      field.filedType === "FILE" &&
      !field.required &&
      (value === "" || value === undefined)
    ) {
      return defaults;
    }
    if (value !== undefined) {
      defaults[field.fieldName] = value;
    }
    return defaults;
  }, {} as Record<string, any>);
};

export const extractComponentFields = (
  source: Record<string, any> | null,
  defaults: Record<string, any>,
  metadataFieldsArray: MetaConfig[],
  hasMetadata: boolean
): Record<string, any> => {
  if (!hasMetadata) {
    return { ...defaults, ...(source || {}) };
  }

  const dataKeys = metadataFieldsArray.map((mf) => mf.dataKey);
  const fields = source ? { ...source } : {};
  dataKeys.forEach((key) => delete fields[key]);

  return { ...defaults, ...fields };
};

export const buildMetadataStates = (
  source: Record<string, any> | null,
  metadataFieldsArray: MetaConfig[],
  defaultRows: any[],
  hasMetadata: boolean
): Record<string, any[]> => {
  if (!hasMetadata) return {};

  return metadataFieldsArray.reduce((states, metaConfig, index) => {
    const { dataKey } = metaConfig;
    const data = source?.[dataKey];
    const defaultRow = defaultRows[index];
    states[dataKey] = data?.length > 0 ? data : defaultRow ? [defaultRow] : [];
    return states;
  }, {} as Record<string, any[]>);
};

export const injectDynamicOptions = (
  metadataFieldsArray: MetaConfig[],
  dynamicOptions: Record<string, any[]>
): MetaConfig[] => {
  return metadataFieldsArray.map((meta) => ({
    ...meta,
    fields: meta.fields?.map((field: any) => ({
      ...field,
      ...(field.dynamicOptions &&
        dynamicOptions[field.dynamicOptions] && {
          options: dynamicOptions[field.dynamicOptions],
        }),
    })),
  }));
};

export const getSourceData = (
  sectionData: Record<string, any> | undefined,
  reduxState: any
): Record<string, any> | null => {
  return sectionData && typeof sectionData === "object" && Object.keys(sectionData).length > 0
    ? sectionData
    : reduxState;
};

export const normalizeEmptyFields = (
  fields: Record<string, any>,
  componentFieldsConfig: any[]
): Record<string, any> => {
  // Empty strings break two kinds of columns:
  //  - number columns reject "" -> send null
  //  - SELECT (enum) columns reject "" -> fall back to the field's defaultValue
  //    (a valid enum value), so the DB and the widget's Zod schema both accept it.
  const numberFieldNames = new Set(
    componentFieldsConfig
      .filter((f) => f.valueType === "number")
      .map((f) => f.fieldName)
  );
  const selectFieldDefaults = new Map(
    componentFieldsConfig
      .filter((f) => f.filedType === "SELECT")
      .map((f) => [f.fieldName, f.defaultValue])
  );

  return Object.entries(fields).reduce((acc, [key, value]) => {
    if (value === "" && numberFieldNames.has(key)) {
      acc[key] = null;
    } else if (value === "" && selectFieldDefaults.has(key)) {
      acc[key] = selectFieldDefaults.get(key) ?? null;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);
};

export const hasFormChanged = (
  prev: { id?: string; componentId?: string; data?: any },
  current: { id: string; componentId?: string; data?: any },
  componentFieldsLength: number
): boolean => {
  const sectionChanged = prev.id !== current.id;
  const componentIdChanged = prev.componentId !== current.componentId;
  const dataChanged = prev.data !== current.data;
  const isInitialMount = prev.id === current.id && componentFieldsLength === 0;

  return sectionChanged || componentIdChanged || dataChanged || isInitialMount;
};

